import { selectRunsheetWithShiftsById, selectScenarioWithShiftsById } from '@/entities/schedules'
import { CollapsibleHeader } from '@/shared/components/GridTable/CollapsibleHeader/CollapsibleHeader'
import { useAppSelector } from '@/shared/store'
import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-premium'
import { EntityId } from '@reduxjs/toolkit'

const runColumns: GridColDef[] = [
  {
    field: 'name',
    headerName: 'Run Name',
    width: 100,
    description: 'Run Name',
  },
  {
    field: 'orderCount',
    headerName: 'Orders',
    width: 80,
    description: 'No of Orders in Run',
  },
  {
    field: 'totalDistance',
    headerName: 'Distance',
    width: 80,
    description: 'Distance Travelled by Run',
  },
  {
    field: 'totalCost',
    headerName: 'Cost',
    width: 80,
    description: 'Cost for doing the Run',
  },
  // Weight columns group
  {
    field: 'weightCapacity',
    headerName: 'Capacity',
    width: 100,
    description: 'Weight Capacity',
  },
  {
    field: 'totalWeight',
    headerName: 'Allocated',
    width: 100,
    description: 'Total Weight of Orders on Run',
  },
  {
    field: 'weightCapUtilization',
    headerName: 'Utilization (%)',
    width: 100,
    valueFormatter: (value) => {
      return value > 0 ? `${(value * 100).toFixed(2)}%` : '0.00%'
    },
    description: '% Weight Utilization',
  },
  // Volume columns group
  {
    field: 'volumeCapacity',
    headerName: 'Capacity',
    width: 100,
    description: 'Volume Capacity',
  },
  {
    field: 'totalVolume',
    headerName: 'Allocated',
    width: 100,
    description: 'Total Volume of Orders on Run',
  },
  {
    field: 'volumeCapUtilization',
    headerName: 'Utilization (%)',
    width: 100,
    valueFormatter: (value) => {
      return value > 0 ? `${(value * 100).toFixed(2)}%` : '0.00%'
    },
    description: '% Volume Utilization',
  },
  // Working Time columns group
  {
    field: 'maxWorkingTime',
    headerName: 'Max',
    width: 100,
    description: 'Max Working Time',
  },
  {
    field: 'totalWorkingTime',
    headerName: 'Allocated',
    width: 100,
    valueFormatter: (value: string) => {
      return value ? value.substr(0, 5) : ''
    },
    description: 'Allocated Working Time',
  },
  {
    field: 'workingTimeCapUtilization',
    headerName: 'Utilization (%)',
    width: 100,
    valueFormatter: (value) => {
      return value > 0 ? `${(value * 100).toFixed(2)}%` : '0.00%'
    },
    description: '% Time Utilization',
  },
]

export const runColumnGroupingModel: GridColumnGroupingModel = [
  {
    groupId: 'basic',
    description: 'Basic run information',
    headerName: 'Run Info',
    renderHeaderGroup: (params) => {
      return <CollapsibleHeader
        groupFields={['name', 'orderCount', 'totalDistance', 'totalCost']}
        numColumnsWhenCollapsed={4}
        {...params}
      />
    },
    children: [
      { field: 'name' },
      { field: 'orderCount' },
      { field: 'totalDistance' },
      { field: 'totalCost' },
    ],
  },
  {
    groupId: 'weight',
    description: 'Weight capacity and utilization',
    headerName: 'Weight',
    renderHeaderGroup: (params) => {
      return <CollapsibleHeader
        groupFields={['weightCapacity', 'totalWeight', 'weightCapUtilization']}
        numColumnsWhenCollapsed={3}
        {...params}
      />
    },
    children: [
      { field: 'weightCapacity' },
      { field: 'totalWeight' },
      { field: 'weightCapUtilization' },
    ],
  },
  {
    groupId: 'volume',
    description: 'Volume capacity and utilization',
    headerName: 'Volume',
    renderHeaderGroup: (params) => {
      return <CollapsibleHeader
        groupFields={['volumeCapacity', 'totalVolume', 'volumeCapUtilization']}
        numColumnsWhenCollapsed={3}
        {...params}
      />
    },
    children: [
      { field: 'volumeCapacity' },
      { field: 'totalVolume' },
      { field: 'volumeCapUtilization' },
    ],
  },
  {
    groupId: 'workingTime',
    description: 'Working time details',
    headerName: 'Working Time',
    renderHeaderGroup: (params) => {
      return <CollapsibleHeader
        groupFields={['maxWorkingTime', 'totalWorkingTime', 'workingTimeCapUtilization']}
        numColumnsWhenCollapsed={3}
        {...params}
      />
    },
    children: [
      { field: 'maxWorkingTime' },
      { field: 'totalWorkingTime' },
      { field: 'workingTimeCapUtilization' },
    ],
  },
]

export const useRunsSummaryTableData = (id: EntityId, layoutType: 'planning' | 'runsheet') => {
  const schedule = useAppSelector(state => 
    layoutType === 'planning' ?
      selectScenarioWithShiftsById(state, id) :
      selectRunsheetWithShiftsById(state, id),
  )

  return {
    scheduleName: schedule?.name || '',
    shifts: schedule?.shifts || [],
    runColumns,
    runColumnGroupingModel,
  }
}